import { useCallback, useEffect, useRef, useState } from 'react';

// Entwurfs-Sicherung fuer Formulare, die bei Sitzungsablauf oder einem
// versehentlich geschlossenen Tab sonst verloren gehen (QA-Befund S-1).
//
// Schluessel: tracking-draft-{scope}-{userId}. Die Bindung an den Nutzer
// ist wichtig, weil sich mehrere Mitarbeiter dasselbe Tablet teilen —
// niemand soll den halbfertigen Entwurf eines anderen vorgesetzt bekommen.
//
// Bewusst kein Auto-Restore: ein Entwurf wird angeboten, nicht eingesetzt.
// Ungefragt wiederhergestellte Formularstaende sind schwer zu erkennen und
// noch schwerer rueckgaengig zu machen.
//
// In localStorage stehen ausschliesslich Formularinhalte, niemals
// Auth-Tokens (CLAUDE.md).

const TTL_TAGE = 7;

type Umschlag<T> = { gespeichert_am: number; daten: T };

function schluessel(scope: string, userId: number | null): string {
  return `tracking-draft-${scope}-${userId ?? 'anon'}`;
}

function lesen<T>(key: string): T | null {
  if (typeof window === 'undefined') return null;
  try {
    const roh = window.localStorage.getItem(key);
    if (roh === null) return null;
    const u = JSON.parse(roh) as Umschlag<T>;
    if (typeof u?.gespeichert_am !== 'number') return null;
    const alterTage = (Date.now() - u.gespeichert_am) / 86_400_000;
    if (alterTage > TTL_TAGE) {
      window.localStorage.removeItem(key);
      return null;
    }
    return u.daten;
  } catch {
    // Kaputter oder fremder Eintrag: lieber ignorieren als abstuerzen.
    return null;
  }
}

function schreiben<T>(key: string, daten: T): void {
  if (typeof window === 'undefined') return;
  try {
    const u: Umschlag<T> = { gespeichert_am: Date.now(), daten };
    window.localStorage.setItem(key, JSON.stringify(u));
  } catch {
    // Quota voll oder Storage gesperrt (privater Modus): Entwuerfe sind
    // ein Komfort, kein Muss — still weitermachen.
  }
}

function entfernen(key: string): void {
  if (typeof window === 'undefined') return;
  try {
    window.localStorage.removeItem(key);
  } catch {
    /* siehe schreiben() */
  }
}

type Optionen<T> = {
  /** Stabiler Name des Formulars, z. B. 'neue-bestellung'. */
  scope: string;
  userId: number | null;
  /** Aktueller Formularstand. */
  wert: T;
  /** Nur speichern, solange das Formular offen ist. */
  aktiv: boolean;
  /** Leere Formulare erzeugen keinen Entwurf. */
  istLeer: (wert: T) => boolean;
  debounceMs?: number;
};

type Ergebnis<T> = {
  /** Gefundener Entwurf, ueber den der Nutzer noch entscheiden muss. */
  angebot: T | null;
  /** Angebot annehmen — der Aufrufer setzt den Wert selbst ein. */
  annehmen: () => T | null;
  /** Angebot ablehnen und Entwurf loeschen. */
  verwerfen: () => void;
  /** Nach erfolgreichem Absenden aufraeumen. */
  loeschen: () => void;
};

export function useDraft<T>({
  scope,
  userId,
  wert,
  aktiv,
  istLeer,
  debounceMs = 600
}: Optionen<T>): Ergebnis<T> {
  const key = schluessel(scope, userId);
  const [angebot, setAngebot] = useState<T | null>(null);
  // Merkt sich, fuer welchen Schluessel schon nachgesehen wurde, damit das
  // Angebot nicht bei jedem Renderdurchlauf neu auftaucht.
  const geprueftRef = useRef<string | null>(null);
  // Spiegelt `angebot` als Ref. Noetig, weil der Speicher-Effekt im selben
  // Commit laeuft wie der Lese-Effekt und den frisch gesetzten State noch
  // nicht sieht — er wuerde den gerade gefundenen Entwurf sonst loeschen,
  // bevor er ueberhaupt angeboten wurde.
  const angebotRef = useRef<T | null>(null);
  const istLeerRef = useRef(istLeer);
  istLeerRef.current = istLeer;

  // Beim Oeffnen einmal nachsehen, ob ein Entwurf herumliegt.
  useEffect(() => {
    if (!aktiv) {
      geprueftRef.current = null;
      angebotRef.current = null;
      setAngebot(null);
      return;
    }
    if (geprueftRef.current === key) return;
    geprueftRef.current = key;
    const gefunden = lesen<T>(key);
    angebotRef.current = gefunden;
    setAngebot(gefunden);
  }, [aktiv, key]);

  // Debounced sichern. Solange ein Angebot offen ist, wird nicht
  // geschrieben — sonst ueberschriebe das leere Formular genau den
  // Entwurf, ueber den der Nutzer noch entscheiden soll.
  useEffect(() => {
    if (!aktiv || angebotRef.current !== null) return;
    if (istLeerRef.current(wert)) {
      entfernen(key);
      return;
    }
    const t = setTimeout(() => schreiben(key, wert), debounceMs);
    return () => clearTimeout(t);
  }, [aktiv, angebot, wert, key, debounceMs]);

  const annehmen = useCallback((): T | null => {
    const daten = angebotRef.current;
    angebotRef.current = null;
    setAngebot(null);
    return daten;
  }, []);

  const verwerfen = useCallback((): void => {
    entfernen(key);
    angebotRef.current = null;
    setAngebot(null);
  }, [key]);

  const loeschen = useCallback((): void => {
    entfernen(key);
    angebotRef.current = null;
    setAngebot(null);
  }, [key]);

  return { angebot, annehmen, verwerfen, loeschen };
}
